/* LOMSIR PDP: no third-party review dependency; no fabricated records. */
(() => {
  const reduceMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const root = document.documentElement;
  const safeURL = value => {
    try { const u = new URL(value, location.origin); return u.protocol === 'https:' ? u.href : null; } catch { return null; }
  };
  function lightbox(host) {
    const dialog = host.querySelector('.lp-lightbox');
    if (!dialog || typeof dialog.showModal !== 'function') return;
    host.addEventListener('click', e => {
      const link = e.target.closest('[data-lp-photo]');
      if (!link || !host.contains(link)) return;
      const url = safeURL(link.getAttribute('href'));
      if (!url) return;
      e.preventDefault();
      const img = dialog.querySelector('img');
      img.src = url;
      img.alt = link.querySelector('img')?.alt || link.getAttribute('aria-label') || 'Product photo';
      dialog.showModal();
    }, { signal: host.abort.signal });
    dialog.querySelector('[data-lp-close]').addEventListener('click', () => dialog.close(), { signal: host.abort.signal });
    dialog.addEventListener('click', e => { if (e.target === dialog) dialog.close(); }, { signal: host.abort.signal });
  }
  class ProductNav extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      this.links = [...this.querySelectorAll('[data-lp-target]')];
      this.header = document.querySelector('.section-header');
      this.resolve = this.resolve.bind(this);
      this.update = this.update.bind(this);
      this.schedule = () => { if (!this.frame) this.frame = requestAnimationFrame(this.update); };
      this.resolve();
      this.addEventListener('click', e => {
        const link = e.target.closest('a'); if (!link) return;
        e.stopPropagation();
        if (link.hasAttribute('data-lp-top')) {
          e.preventDefault(); scrollTo({ top: 0, behavior: reduceMotion() ? 'instant' : 'smooth' }); return;
        }
        const target = this.targets.get(link); if (!target) return;
        e.preventDefault();
        let ancestor = target.closest('details');
        while (ancestor) { ancestor.open = true; ancestor = ancestor.parentElement?.closest('details'); }
        this.update();
        const offset = this.headerOffset + this.getBoundingClientRect().height + 24;
        scrollTo({ top: Math.max(0, scrollY + target.getBoundingClientRect().top - offset), behavior: reduceMotion() ? 'instant' : 'smooth' });
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
        history.replaceState(null, '', '#' + target.id);
      }, { signal: this.abort.signal, capture: true });
      addEventListener('scroll', this.schedule, { passive: true, signal: this.abort.signal });
      addEventListener('resize', this.schedule, { passive: true, signal: this.abort.signal });
      document.addEventListener('shopify:section:load', this.resolve, { signal: this.abort.signal });
      this.resizeObserver = new ResizeObserver(this.schedule);
      if (this.header) this.resizeObserver.observe(this.header);
      this.resizeObserver.observe(this);
      this.update();
    }
    resolve() {
      this.targets = new Map();
      const main = document.querySelector('product-info[id^="MainProduct-"]');
      const description = main?.querySelector('.lomsir-product-description-wrap');
      if (description && !description.id) description.id = 'lomsir-details';
      const candidates = description ? [...description.querySelectorAll('h2,h3,h4,h5,[class*="section-title"],[class*="info-title"]')] : [];
      const patterns = {
        ingredients: /ingredient|supplement facts|formula|formulation|配方|成分/i,
        directions: /suggested use|how to (use|take)|directions|recommended use|用法/i,
        warnings: /warn|caution|safety|注意/i,
        faqs: /faq|frequently asked|common questions/i
      };
      this.links.forEach(link => {
        const key = link.dataset.lpTarget;
        let target = link.dataset.lpCustom ? document.getElementById(link.dataset.lpCustom.replace(/^#/, '')) : null;
        if (!target) target = key === 'overview' ? main : key === 'details' ? description : document.getElementById('lomsir-' + key);
        if (!target && patterns[key]) target = candidates.find(el => patterns[key].test(el.textContent.trim()));
        link.hidden = !target;
        if (target) {
          if (!target.id) target.id = 'lomsir-' + key;
          target.setAttribute('data-lp-anchor', key);
          link.href = '#' + target.id;
          this.targets.set(link, target);
        }
      });
      this.schedule?.();
    }
    update() {
      this.frame = null;
      const box = this.header?.getBoundingClientRect();
      this.headerOffset = box && box.top <= 2 ? Math.max(0, Math.min(box.height, box.bottom)) : 0;
      root.style.setProperty('--lp-header-offset', this.headerOffset + 'px');
      root.style.setProperty('--lp-nav-height', this.getBoundingClientRect().height + 'px');
      const threshold = this.headerOffset + this.getBoundingClientRect().height + 65;
      const ordered = [...this.targets].filter(([,el]) => el.getClientRects().length).sort((a,b) => a[1].getBoundingClientRect().top - b[1].getBoundingClientRect().top);
      let active = ordered[0]?.[0];
      for (const [link, el] of ordered) { if (el.getBoundingClientRect().top <= threshold) active = link; }
      this.links.forEach(link => { if (link === active) link.setAttribute('aria-current','location'); else link.removeAttribute('aria-current'); });
      if (active && this.active !== active) {
        this.active = active;
        const rail = this.querySelector('.lp-nav__links'), item = active.getBoundingClientRect(), bounds = rail.getBoundingClientRect();
        if (item.left < bounds.left || item.right > bounds.right) rail.scrollLeft += item.left - bounds.left - (bounds.width - item.width) / 2;
      }
      const content = document.querySelector('#MainContent');
      if (content) {
        const total = content.offsetHeight - innerHeight;
        const progress = total > 0 ? Math.min(1, Math.max(0, (scrollY - content.offsetTop) / total)) : 0;
        this.querySelector('.lp-nav__progress span').style.transform = 'scaleX(' + progress + ')';
      }
    }
    disconnectedCallback() { this.abort?.abort(); this.resizeObserver?.disconnect(); cancelAnimationFrame(this.frame); this.abort = null; }
  }
  class BrandNotes extends HTMLElement {
    connectedCallback() {
      if (this.abort) return; this.abort = new AbortController();
      lightbox(this);
      this.items = [...this.querySelectorAll('[data-lp-note]')];
      if (!this.items.length) return;
      this.limit = Number(this.dataset.pageSize) || 6; this.page = this.limit;
      this.querySelector('[data-lp-filters]').hidden = false;
      this.render = () => {
        const query = this.querySelector('[data-lp-search]').value.trim().toLocaleLowerCase();
        const topic = this.querySelector('[data-lp-topic]').value;
        const filtered = this.items.filter(el => (!query || el.textContent.toLocaleLowerCase().includes(query)) && (!topic || el.dataset.topic === topic));
        this.items.forEach(el => el.hidden = true); filtered.slice(0,this.page).forEach(el => el.hidden = false);
        this.querySelector('[data-lp-count]').textContent = filtered.length + (filtered.length === 1 ? ' brand note' : ' brand notes');
        this.querySelector('[data-lp-empty]').hidden = filtered.length > 0;
        this.querySelector('[data-lp-more]').hidden = filtered.length <= this.page;
      };
      ['[data-lp-search]','[data-lp-topic]'].forEach(selector => this.querySelector(selector).addEventListener('input', () => { this.page = this.limit; this.render(); }, { signal: this.abort.signal }));
      this.querySelector('[data-lp-more]').addEventListener('click', () => { this.page += this.limit; this.render(); }, { signal: this.abort.signal });
      this.render();
    }
    disconnectedCallback() { this.abort?.abort(); this.abort = null; }
  }
  class Reviews extends HTMLElement {
    connectedCallback() {
      if (this.abort) return; this.abort = new AbortController();
      this.apiReady = false;
      this.form = this.querySelector('form');
      this.details = this.querySelector('.lp-feedback');
      this.status = this.querySelector('[data-lp-review-status]');
      this.list = this.querySelector('[data-lp-review-list]');
      this.formStatus = this.querySelector('[data-lp-api-status]');
      lightbox(this);
      this.querySelector('[data-lp-write]').addEventListener('click', e => {
        e.preventDefault(); this.details.open = true;
        this.form.scrollIntoView({ behavior: reduceMotion() ? 'instant' : 'smooth', block:'start' });
        this.form.querySelector('input[autocomplete="nickname"]').focus({preventScroll:true});
      }, { signal:this.abort.signal });
      if (this.querySelector('[data-lp-submitted],.lp-form-status--error') || location.hash === '#LomsirReviewForm' || location.search.includes('contact_posted=true')) this.details.open = true;
      const value = (this.dataset.endpoint || '').trim().replace(/\/$/, '');
      // Allow only a merchant's same-origin Shopify app proxy, never an arbitrary upload host.
      if (!/^\/apps\/[a-zA-Z0-9][a-zA-Z0-9/_-]*$/.test(value)) return;
      this.endpoint = value;
      this.page = 1;
      this.querySelectorAll('[data-lp-review-rating],[data-lp-review-sort]').forEach(el => el.addEventListener('change', () => { this.page=1; this.load(); }, {signal:this.abort.signal}));
      this.querySelector('[data-lp-review-search]').addEventListener('input', () => {
        clearTimeout(this.searchTimer); this.searchTimer=setTimeout(() => {this.page=1;this.load();},350);
      }, {signal:this.abort.signal});
      this.querySelectorAll('[data-lp-rating-bar]').forEach(el => el.addEventListener('click', () => {
        this.querySelector('[data-lp-review-rating]').value=el.dataset.lpRatingBar;this.page=1;this.load();
      }, {signal:this.abort.signal}));
      this.querySelector('[data-lp-review-more]').addEventListener('click', () => {this.page+=1;this.load(true);}, {signal:this.abort.signal});
      this.load();
    }
    async load(append=false) {
      this.fetchAbort?.abort(); const controller=new AbortController();this.fetchAbort=controller;
      const timeout=setTimeout(()=>controller.abort(),10000);
      const url=new URL(this.endpoint, location.origin);
      url.search=new URLSearchParams({product_id:this.dataset.productId,page:String(this.page),search:this.querySelector('[data-lp-review-search]').value,rating:this.querySelector('[data-lp-review-rating]').value,sort:this.querySelector('[data-lp-review-sort]').value}).toString();
      this.status.textContent='Loading customer reviews…';this.status.hidden=false;
      try {
        const response=await fetch(url, {signal:controller.signal,credentials:'same-origin',headers:{Accept:'application/json'}});
        if(!response.ok) throw new Error('Reviews unavailable');
        const data=await response.json();
        if(data.product_id!==this.dataset.productId || !Array.isArray(data.reviews) || !data.aggregate || !Number.isInteger(data.aggregate.count) || data.aggregate.count<0) throw new Error('Invalid review data');
        if(this.fetchAbort!==controller || !this.isConnected) return;
        const count=data.aggregate.count, avg=Number(data.aggregate.average);
        if(count>0 && (!Number.isFinite(avg)||avg<1||avg>5)) throw new Error('Invalid rating');
        const frequencies=[5,4,3,2,1].map(n=>Number(data.aggregate.histogram?.[String(n)]));
        if(frequencies.some(n=>!Number.isInteger(n)||n<0)||frequencies.reduce((a,b)=>a+b,0)!==count) throw new Error('Invalid histogram');
        this.querySelector('[data-lp-average]').textContent=count?avg.toFixed(1):'—';
        this.querySelector('[data-lp-total]').textContent=count+' published '+(count===1?'review':'reviews');
        const stars=this.querySelector('[data-lp-stars]');
        stars.classList.toggle('lp-stars--empty',!count);
        stars.setAttribute('aria-label',count?avg.toFixed(1)+' out of 5 stars':'No customer rating yet');
        if(count){stars.style.background='linear-gradient(90deg,#20241f '+avg/5*100+'%,#d4d9d0 '+avg/5*100+'%)';stars.style.backgroundClip='text';stars.style.color='transparent';}
        [5,4,3,2,1].forEach((n,i)=>{
          this.querySelector('[data-lp-frequency="'+n+'"]').textContent=String(frequencies[i]);
          this.querySelector('[data-lp-bar="'+n+'"]').style.width=(count?frequencies[i]/count*100:0)+'%';
        });
        this.querySelectorAll('[data-lp-review-search],[data-lp-review-rating],[data-lp-review-sort],[data-lp-rating-bar]').forEach(el=>el.disabled=false);
        if(!append) this.list.replaceChildren();
        data.reviews.forEach(review=>this.renderReview(review));
        this.status.hidden=data.reviews.length>0;
        this.status.textContent=count?'No reviews match your filters.':'No published reviews yet. Share your experience below.';
        this.querySelector('[data-lp-review-more]').hidden=data.has_more!==true;
        // Direct uploads remain unavailable unless the deployed service explicitly advertises them.
        if(data.capabilities?.submissions===true && !this.apiReady) this.enableSubmission(data.capabilities.uploads===true);
      } catch(error) {
        if(this.fetchAbort!==controller || !this.isConnected) return;
        this.status.textContent='Customer reviews are temporarily unavailable. You can still send feedback below.';
        if(append)this.page=Math.max(1,this.page-1);
        this.querySelector('[data-lp-review-more]').hidden=true;
      } finally {clearTimeout(timeout);}
    }
    renderReview(review) {
      if(!review || !Number.isInteger(review.rating)||review.rating<1||review.rating>5||typeof review.body!=='string'||typeof review.display_name!=='string') return;
      const make=(tag,className,text)=>{const el=document.createElement(tag);if(className)el.className=className;if(text!==undefined)el.textContent=text;return el;};
      const article=make('article','lp-note');
      const author=make('div','lp-note__author');
      author.append(make('span','lp-note__monogram',review.display_name.slice(0,1).toUpperCase()),make('strong','',review.display_name.slice(0,80)));
      // This flag must originate from server-side order/product verification, never a form checkbox.
      author.append(make('span','lp-note__disclosure',review.verification==='verified_purchase'?'Verified purchase':'Purchase not verified'));
      if(review.incentivized===true)author.append(make('span','lp-note__disclosure','Product received free or with incentive'));
      const main=make('div','lp-note__main'),head=make('div','lp-note__heading');
      head.append(make('h3','',String(review.title||'Customer review').slice(0,150)));
      const date=new Date(review.created_at);
      if(Number.isFinite(date.getTime())){const time=make('time','',date.toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}));time.dateTime=date.toISOString();head.append(time);}
      const stars=make('p','lp-stars','★'.repeat(review.rating)+'☆'.repeat(5-review.rating));stars.setAttribute('aria-label',review.rating+' out of 5 stars');
      const body=make('div','lp-note__body',review.body.slice(0,5000));body.style.whiteSpace='pre-line';
      main.append(head,stars,body);
      if(Array.isArray(review.photos)){
        const photos=make('div','lp-note__images');
        review.photos.slice(0,5).forEach(photo=>{
          const href=safeURL(photo.url),thumb=safeURL(photo.thumbnail_url||photo.url);if(!href||!thumb)return;
          const a=make('a');a.href=href;a.dataset.lpPhoto='';a.setAttribute('aria-label','View customer photo');
          const image=make('img');image.src=thumb;image.alt='Customer photo of this product';image.loading='lazy';a.append(image);photos.append(a);
        });main.append(photos);
      }
      if(typeof review.merchant_reply==='string'&&review.merchant_reply){
        const reply=make('div','lp-form-status');reply.append(make('strong','','Response from LOMSIR'),make('div','',review.merchant_reply.slice(0,3000)));main.append(reply);
      }
      // Private email/order fields are intentionally never copied into DOM or data attributes.
      article.append(author,main);this.list.append(article);
    }
    enableSubmission(uploads) {
      this.apiReady=true;
      const input=this.querySelector('[data-lp-upload]');
      if(uploads){
        this.querySelector('[data-lp-upload-area]').hidden=false;input.disabled=false;
        this.querySelector('[data-lp-photo-url]').hidden=true;
        input.addEventListener('change',()=>{
          const files=[...input.files],valid=files.length<=5&&files.every(f=>['image/jpeg','image/png','image/webp'].includes(f.type)&&f.size<=5*1024*1024);
          input.setCustomValidity(valid?'':'Choose up to 5 JPG, PNG or WebP files, each no larger than 5 MB.');
          this.previewURLs?.forEach(url=>URL.revokeObjectURL(url));this.previewURLs=[];
          const preview=this.querySelector('[data-lp-upload-preview]');preview.replaceChildren();
          if(valid)files.forEach(file=>{const image=document.createElement('img'),url=URL.createObjectURL(file);this.previewURLs.push(url);image.src=url;image.alt='Selected photo preview';preview.append(image);});
          input.reportValidity();
        },{signal:this.abort.signal});
      }
      this.form.addEventListener('submit',async e=>{
        e.preventDefault();if(!this.form.reportValidity())return;
        const payload=new FormData(this.form);payload.set('product_id',this.dataset.productId);
        if(uploads)[...input.files].forEach(file=>payload.append('photos',file));
        const button=this.form.querySelector('[type="submit"]');button.disabled=true;
        this.formStatus.hidden=false;this.formStatus.textContent='Sending your feedback…';
        this.submitAbort?.abort();this.submitAbort=new AbortController();const timer=setTimeout(()=>this.submitAbort.abort(),30000);
        try{
          const response=await fetch(this.endpoint,{method:'POST',body:payload,credentials:'same-origin',signal:this.submitAbort.signal,headers:{Accept:'application/json'}});
          if(!response.ok)throw new Error();
          const data=await response.json();if(data.status!=='pending_moderation')throw new Error();
          this.formStatus.textContent='Thank you. Your feedback has been received and is awaiting review. It is not yet public.';
          this.form.reset();this.previewURLs?.forEach(url=>URL.revokeObjectURL(url));this.previewURLs=[];
          this.querySelector('[data-lp-upload-preview]').replaceChildren();
        }catch{this.formStatus.textContent='We could not confirm receipt. Your feedback has been kept in this form. Please try again later.';}
        finally{clearTimeout(timer);button.disabled=false;}
      },{signal:this.abort.signal});
    }
    disconnectedCallback(){this.abort?.abort();this.fetchAbort?.abort();this.submitAbort?.abort();clearTimeout(this.searchTimer);this.previewURLs?.forEach(url=>URL.revokeObjectURL(url));this.abort=null;}
  }
  if(!customElements.get('lomsir-product-nav'))customElements.define('lomsir-product-nav',ProductNav);
  if(!customElements.get('lomsir-brand-notes'))customElements.define('lomsir-brand-notes',BrandNotes);
  if(!customElements.get('lomsir-reviews'))customElements.define('lomsir-reviews',Reviews);
})();

